import * as Sentry from '@sentry/react';
import React, { useContext, useState } from 'react';
import { NotificationType, TSnackbarNotificationContext } from '../../../types';
import SnackbarNotification from './SnackbarNotification';

const NotificationContext = React.createContext<TSnackbarNotificationContext>(
  {} as any
);

interface Props {
  children: any;
}

export type State = {
  type: NotificationType;
  isOpen: boolean;
  autoHideDuration: number | null;
  message: React.ReactNode;
  action: React.ReactNode;
  error: Error | undefined;
};

const DEFAULT_AUTO_HIDE = 6000;

const SnackbarNotificationProvider = ({ children }: Props) => {
  const [state, setState] = useState<State>({
    type: 'info',
    isOpen: false,
    autoHideDuration: DEFAULT_AUTO_HIDE,
    error: undefined,
    action: null,
    message: '',
  });

  const show = (
    type: NotificationType,
    message: React.ReactNode,
    autoHideDuration?: number | null,
    action?: React.ReactNode,
    error?: Error
  ) => {
    setState({
      type,
      message,
      autoHideDuration:
        autoHideDuration || autoHideDuration === null
          ? autoHideDuration
          : DEFAULT_AUTO_HIDE,
      error: error,
      action: action || null,
      isOpen: true,
    });
  };

  const info = (
    message: React.ReactNode,
    autoHideDuration?: number | null,
    action?: React.ReactNode
  ) => {
    show('info', message || '', autoHideDuration, action);
  };

  const success = (
    message: React.ReactNode,
    autoHideDuration?: number | null,
    action?: React.ReactNode
  ) => {
    show('success', message || '', autoHideDuration, action);
  };

  const warning = (
    message: React.ReactNode,
    autoHideDuration?: number | null,
    action?: React.ReactNode
  ) => {
    show('warning', message || '', autoHideDuration, action);
  };

  const error = ({
    message,
    autoHideDuration,
    error,
    action,
    context,
  }: {
    message: string;
    autoHideDuration?: number | null;
    error?: Error;
    action?: React.ReactNode;
    context?: object;
  }) => {
    Sentry.withScope((scope) => {
      scope.setTag('SnackbarNotificationError', 'yes');
      Sentry.captureException(error || message, context);
    });
    show('error', message || '', autoHideDuration, action, error);
  };

  const close = () => {
    setState((prevState) => ({
      autoHideDuration: DEFAULT_AUTO_HIDE,
      message: '',
      type: prevState.type,
      isOpen: false,
      error: undefined,
      action: null,
    }));
  };

  return (
    <NotificationContext.Provider
      value={{
        ...state,
        show,
        info,
        success,
        warning,
        error,
        close,
      }}
    >
      <SnackbarNotification />
      {children}
    </NotificationContext.Provider>
  );
};

const useSnackbarNotification = () => useContext(NotificationContext);

export {
  SnackbarNotificationProvider,
  NotificationContext as SnackbarNotificationContext,
  useSnackbarNotification,
  TSnackbarNotificationContext,
};
